import { CheckCircle2, Clipboard, KeyRound } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { exportGroupKey, isBackupConfirmed, loadGroupKey, markBackupConfirmed } from "./keys.js";

export async function copyBackupToClipboard(userId: string): Promise<string | null> {
  const json = exportGroupKey(userId);
  if (!json) return null;
  try {
    await navigator.clipboard.writeText(json);
    return null;
  } catch {
    return json;
  }
}

export function BackupBanner({ userId }: { userId: string }) {
  const [confirmed, setConfirmed] = useState(() => isBackupConfirmed(userId));
  const [copied, setCopied] = useState(false);
  const [fallback, setFallback] = useState<string | null>(null);
  if (confirmed || !loadGroupKey(userId)) return null;

  async function copy() {
    const shown = await copyBackupToClipboard(userId);
    setFallback(shown);
    setCopied(true);
  }
  function confirm() {
    markBackupConfirmed(userId);
    setConfirmed(true);
  }

  return (
    <div className="card warn">
      <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <KeyRound size={18} className="icon" />
        Save your decryption key in a password manager
      </h2>
      <p>
        Your decryption key lives only in this browser. If this browser's data is cleared, or you
        switch devices, <strong>this key is the only way back in</strong> — without it your
        recordings cannot be opened by anyone.
      </p>
      <ol className="muted" style={{ paddingLeft: 20, margin: "0 0 12px" }}>
        <li>Copy the key.</li>
        <li>
          Paste it into a password manager (1Password, Bitwarden, iCloud Keychain, Google Password
          Manager…) as a secure note or password entry.
        </li>
        <li>Come back and confirm.</li>
      </ol>
      <p className="muted" style={{ marginTop: 0 }}>
        This is not your passkey. The passkey only signs you in.{" "}
        <Link to="/guide#keys">What is the difference?</Link>
      </p>
      <div className="row">
        <button onClick={copy} style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Clipboard size={15} />
          Copy key
        </button>
        <button
          className="primary"
          onClick={confirm}
          disabled={!copied}
          style={{ display: "flex", alignItems: "center", gap: 6 }}
        >
          <CheckCircle2 size={15} />
          I've saved it
        </button>
      </div>
      {copied && !fallback && (
        <p className="muted">Copied. Paste it into your password manager now.</p>
      )}
      {fallback && (
        <p className="muted" style={{ wordBreak: "break-all" }}>
          Could not reach the clipboard. Copy this by hand: {fallback}
        </p>
      )}
    </div>
  );
}
